import {CanActivateChildFn, CanActivateFn, Router} from '@angular/router';
import {inject, Signal} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {User, CompanyApiService} from 'patja-api';
import {AuthService} from '../services/auth.service';
import {RoutePaths} from '../../app.routes';
import {CompanyUserFeature} from '../utils/company-user-ui-features';
import {CompaniesService} from '../services/companies.service';
import {CompanyUserUiFeaturesService} from '../services/company-user-ui-features.service';

type FeatureRouteData = {requiredFeature?: CompanyUserFeature};

async function checkFeatureAccess(routeData: FeatureRouteData): Promise<boolean> {
  const authService: AuthService = inject(AuthService);
  const companiesService: CompaniesService = inject(CompaniesService);
  const companyUserUiFeaturesService: CompanyUserUiFeaturesService = inject(
    CompanyUserUiFeaturesService,
  );
  const companyApiService: CompanyApiService = inject(CompanyApiService);
  const router: Router = inject(Router);
  const user: Signal<User | null> = await firstValueFrom(authService.getUser());

  // Applies to company users (own settings within company modules) and accountants
  // (union of client companies' modules). Admins unaffected.
  const currentUser = user();
  if (!currentUser?.isCustomer() && !currentUser?.isAccountant()) {
    return true;
  }

  const requiredFeature = routeData.requiredFeature;
  if (!requiredFeature) {
    return true;
  }

  if (currentUser.isAccountant()) {
    await companyUserUiFeaturesService.loadForAccountant(
      currentUser.activeAccountingOfficeId ?? null,
    );
    if (companyUserUiFeaturesService.isEnabled(requiredFeature)) {
      return true;
    }
    await router.navigateByUrl(RoutePaths.Home);
    return false;
  }

  // Check if companies are already loaded
  const companies = companiesService.getCompanies()();

  // If companies are not loaded, fetch them
  if (companies.length === 0 && user()?.id) {
    try {
      const fetchedCompanies = await firstValueFrom(
        companyApiService.getCompaniesByUser(user()!.id),
      );
      companiesService.setCompanies(fetchedCompanies);
    } catch (error) {
      await router.navigateByUrl(RoutePaths.Home);
      return false;
    }
  }

  const companyId = companiesService.getSelectedCompany()()?.id;
  if (!companyId) {
    await router.navigateByUrl(RoutePaths.Home);
    return false;
  }

  await companyUserUiFeaturesService.loadForCompany(companyId);

  const isEnabled = companyUserUiFeaturesService.isEnabled(requiredFeature);
  if (isEnabled) {
    return true;
  }

  await router.navigateByUrl(RoutePaths.Home);
  return false;
}

export const uiFeatureGuard: CanActivateFn = async route => {
  return await checkFeatureAccess(route.data as FeatureRouteData);
};

export const uiFeatureChildGuard: CanActivateChildFn = async route => {
  return await checkFeatureAccess(route.data as FeatureRouteData);
};
